<template>
    <div class="admin-quotes-page">
      <!-- User Filter (No Header) -->
      <div class="user-filter-container">
        <div class="user-filter">
          <label class="filter-label">Filter by User:</label>
          <b-form-select
            v-model="selectedUserId"
            :options="userOptions"
            class="user-select"
            @change="onUserFilterChange"
          />
        </div>
      </div>

    <!-- Notifications Banner -->
    <div v-if="showSalesLeadAlert" class="alert alert-info alert-dismissible fade show" role="alert">
      <div class="d-flex align-items-center">
        <i class="fas fa-info-circle me-2"></i>
        <div class="flex-grow-1">
          <strong>New Sales Lead Alert:</strong> You have unassigned leads that need attention.
        </div>
        <router-link 
          :to="`/app/sales/leads`" 
          class="btn btn-sm btn-outline-primary me-2"
        >
          View Leads
        </router-link>
      </div>
      <button 
        type="button" 
        class="btn-close" 
        @click="showSalesLeadAlert = false"
        aria-label="Close"
      ></button>
    </div>

        <!-- Quote Statistics -->
        <QuoteStatistics 
          v-if="!loadingStats && quoteStats"
          :stats="quoteStats"
          :selected-user-id="selectedUserId"
          admin-mode
          :key="selectedUserId"
        />
        
        <!-- Loading State -->
        <div v-else-if="loadingStats" class="loading-container">
          <div class="d-flex justify-content-center align-items-center py-5">
            <b-spinner variant="primary" class="me-3"></b-spinner>
            <span class="text-muted">Loading statistics...</span>
          </div>
        </div>
        
        <!-- Error State -->
        <div v-else class="error-container">
          <div class="alert alert-warning text-center">
            <i class="fas fa-exclamation-triangle me-2"></i>
            Unable to load statistics. Please try refreshing the page.
          </div>
        </div>

    <!-- Follow-up Section -->
    <div class="row mt-4">
      <div class="col-12">
        <h5 class="section-title">Follow-up Required</h5>
        <QuoteFollowUpList 
          admin-mode
          :filter-user-email="selectedUserEmail"
          :key="`followup-${selectedUserId}`"
        />
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import QuoteStatistics from '@/components/QuoteStatistics.vue'
import QuoteFollowUpList from '@/components/QuoteFollowUpList.vue'

export default {
  name: 'AdminQuotesPage',
  components: {
    QuoteStatistics,
    QuoteFollowUpList
  },
  data() {
    return {
      quoteStats: null,
      loadingStats: true, // Start with loading true
      showSalesLeadAlert: false,
      loadingNotifications: false,
      selectedUserId: 'all',
      users: [],
      loadingUsers: false
    }
  },
  computed: {
    userOptions() {
      const options = [
        { value: 'all', text: 'All Users' }
      ]
      
      if (this.users && this.users.length > 0) {
        this.users.forEach(user => {
          options.push({
            value: user.email,
            text: `${user.firstName} ${user.lastName} (${user.type})`
          })
        })
      }
      
      return options
    },
    selectedUserEmail() {
      return this.selectedUserId === 'all' ? null : this.selectedUserId
    }
  },
  async mounted() {
    await Promise.all([
      this.loadUsers(),
      this.loadQuoteStats(),
      this.checkForSalesLeadsAlert()
    ])
  },
  methods: {
    async loadUsers() {
      this.loadingUsers = true
      try {
        const response = await axios.get('/services/v2/users/list')
        this.users = response.data.users
      } catch (error) {
        console.error('Error loading users:', error)
      } finally {
        this.loadingUsers = false
      }
    },

    async loadQuoteStats() {
      this.loadingStats = true
      try {
        const params = { adminMode: 'true' }
        if (this.selectedUserEmail) {
          params.userEmail = this.selectedUserEmail
        }
        
        const response = await axios.get('/services/v2/quotes/stats', { params })
        this.quoteStats = response.data
      } catch (error) {
        console.error('Error loading quote stats:', error)
      } finally {
        this.loadingStats = false
      }
    },

    onUserFilterChange() {
      // Reload stats when user filter changes
      this.loadQuoteStats()
    },

    async checkForSalesLeadsAlert() {
      this.loadingNotifications = true
      try {
        const response = await axios.get('/services/sales-leads/alert')
        this.showSalesLeadAlert = response.data.showAlert
      } catch (error) {
        console.error('Error checking sales lead alert:', error)
      } finally {
        this.loadingNotifications = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
  .admin-quotes-page {
    .user-filter-container {
      display: flex;
      justify-content: flex-end;
      margin-bottom: 1.5rem;
    }

    .user-filter {
      display: flex;
      align-items: center;
      gap: 0.75rem;
    }

  .filter-label {
    margin: 0;
    font-weight: 500;
    color: #6c757d;
    white-space: nowrap;
  }

  .user-select {
    min-width: 200px;
    border: 1px solid #ced4da;
    border-radius: 6px;
    
    &:focus {
      border-color: #007bff;
      box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
    }
  }

  .section-title {
    color: #495057;
    font-weight: 600;
    margin-bottom: 1rem;
    padding-bottom: 0.5rem;
    border-bottom: 2px solid #e9ecef;
  }

  .alert {
    border-radius: 8px;
    border: none;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    margin-bottom: 1.5rem;
  }
}</style>
